




import {Component, Vue, Prop} from 'vue-property-decorator';
import {Chart} from 'highcharts-vue';
import Highcharts from 'highcharts';
import boost from 'highcharts/modules/boost';
import {getFormattedDateWithCondition} from '@u/dateTimeUtils';

boost(Highcharts);

@Component({
  components: {
    highcharts: Chart,
  },
})
export default class MachineWorkingDiagram extends Vue {
  //Данные для графика
  @Prop({required: true, type: Array})
  states!: [];

  @Prop({required: false, type: Array})
  errors!: [];

  //Y-координаты серий
  @Prop({required: true, type: Array})
  seriesY!: [];

  //Пороговое значение масштаба по оси X
  @Prop({type: Number, default: 1000})
  xMinScaleValue!: number;

  //Список отображаемых полигонов
  rects: any[] = [];

  //Исходная область графика
  extremes: any;

  //Цвет для разделительных линий между сериями
  splitLineColor = 'rgba(0,0,0,0.0)';

  //Объект графика
  getChart() {
    return (this.$refs.chart as any).chart;
  }

  mounted() {
    this.onChartMounted();
  }

  //Объект конфигурации графика
  get chartOptions() {
    let yMax = 10;
    const series = [];
    //порядок добавления серий важен для прорисовки
    series.push({
      id: 'main',
      type: 'line',
      color: this.splitLineColor,
      data: this.states,
    });

    if (this.errors) {
      series.push({
        id: 'additional',
        type: 'line',
        color: this.splitLineColor,
        data: this.errors,
      });
      yMax = 20;
    }
    return {
      credits: {
        enabled: false,
      },
      chart: {
        zoomType: 'x',
        events: {
          render: this.onRender,
          selection: this.onSelection,
          redraw: this.onRedraw,
        },
        animation: false,
        panning: true,
        panKey: 'shift',
      },
      navigation: {
        buttonOptions: {
          enabled: false,
        },
      },
      plotOptions: {
        series: {
          turboThreshold: 0,
          boostThreshold: 0,
          states: {
            hover: {
              enabled: false,
              halo: {
                size: 0,
              },
            },
          },
          enableMouseTracking: false,
        },
        line: {
          turboThreshold: 0,
          boostThreshold: 0,
          animation: {
            duration: 0,
          },
          marker: {
            enabled: false,
            radius: 0,
          },
        },
      },
      xAxis: {
        labels: {
          formatter: (e: any) => {
            const extremes = e.chart.xAxis[0].getExtremes();
            const diff = extremes.max - extremes.min;
            return getFormattedDateWithCondition(e.value, diff);
          },
        },
        title: {
          text: 'Время',
        },
        gridLineWidth: 0,
      },
      yAxis: {
        visible: false,
        min: 0,
        max: yMax,
        title: {
          enabled: false,
        },
        gridLineWidth: 0,
      },
      tooltip: {
        enabled: false,
      },
      title: {
        text: '',
      },
      legend: {
        enabled: false,
      },
      series,
    };
  }

  //Обработчик перерисовки графика
  onRedraw() {
    //Прорисовываем полигоны
    const chart = this.getChart();
    if (chart) {
      this.drawRects(chart);
    }
  }

  //Обработчик загрузки графика
  onChartMounted() {
    //Сохраняем исходную область видимости
    //Прорисовываем полигоны
    const chart = this.getChart();
    if (chart) {
      this.extremes = chart.xAxis[0].getExtremes();
      this.drawRects(chart);
    }
  }

  //Обработчик прорисовки графика
  onRender() {
    //Скрываем кнопку сброса зума
    const chart = this.getChart();
    if (chart && chart.resetZoomButton) {
      chart.resetZoomButton.hide();
    }
  }

  //Обработчик зума
  onSelection(e: any) {
    //Если интервал зума больше порогового значения - выполняем зум
    e.preventDefault();
    if (e.xAxis.length > 0 && e.xAxis[0]) {
      const startDateTime = e.xAxis[0].min;
      const endDateTime = e.xAxis[0].max;
      if (Math.abs(startDateTime - endDateTime) > this.xMinScaleValue) {
        e.target.xAxis[0].setExtremes(startDateTime, endDateTime);
      }
    }
  }

  //Обработчик двойного щелчка
  onDblClick() {
    //По двойному щелчку получаем текущую область графика и сбрасываем зум если она не равна исходной
    const chart = this.getChart();
    const current = chart.xAxis[0].getExtremes();
    if (current.min != this.extremes.min || current.max != this.extremes.max) {
      chart.xAxis[0].setExtremes(this.extremes.min, this.extremes.max);
    }
  }

  //Прорисовка полигонов
  drawRects(chart: any) {
    this.rects.forEach(x => x.destroy());
    this.rects = [];
    const xAxis = chart.xAxis[0],
      yAxis = chart.yAxis[0];
    const extremes = xAxis.getExtremes();

    chart.series.forEach((s: any, sIndex: any) => {
      const extremePoints: any[] = [];
      let lastBeforePoint: any = null;

      //Находим точки в отображаемом интервале
      const sPointsLength = s.points.length;
      s.points.forEach((p: any) => {
        if (p.x >= extremes.min && p.x <= extremes.max) {
          extremePoints.push(p);
        } else {
          if (p.x < extremes.min && (!lastBeforePoint || lastBeforePoint.x <= p.x)) {
            lastBeforePoint = p;
          }
        }
      });

      const extremePointsLength = extremePoints.length;
      if (extremePointsLength > 0) {
        //Прорисовываем полигоны
        const barHeight = yAxis.toPixels(this.seriesY[sIndex]) - yAxis.toPixels(extremePoints[0].y);
        for (let i = 0; i < extremePointsLength - 1; ++i) {
          const p = extremePoints[i];
          if (!p.elem) continue;

          const p1 = extremePoints[i + 1];
          const rect = chart.renderer
            .rect(
              xAxis.toPixels(p.x),
              yAxis.toPixels(p.y),
              xAxis.toPixels(p1.x) - xAxis.toPixels(p.x) + 1,
              barHeight,
              0,
            )
            .attr({
              fill: p.elem.color,
              zIndex: 0,
            })
            .add();
          this.rects.push(rect);
        }

        //Прорисовываем полигон от границы интервала до первой точки
        if (lastBeforePoint && lastBeforePoint.elem) {
          const p = extremePoints[0];
          const firstRect = chart.renderer
            .rect(
              xAxis.toPixels(extremes.min),
              yAxis.toPixels(p.y),
              xAxis.toPixels(p.x) - xAxis.toPixels(extremes.min) + 1,
              barHeight,
              0,
            )
            .attr({
              fill: lastBeforePoint.elem.color,
              zIndex: 0,
            })
            .add();
          this.rects.push(firstRect);
        }

        //Прорисовываем полигон от последней точки до границы интервала
        const pl = extremePoints[extremePointsLength - 1];
        if (pl.index != s.points[sPointsLength - 1].index && pl.elem) {
          const lastRect = chart.renderer
            .rect(
              xAxis.toPixels(pl.x),
              yAxis.toPixels(pl.y),
              xAxis.toPixels(extremes.max) - xAxis.toPixels(pl.x) + 1,
              barHeight,
              0,
            )
            .attr({
              fill: pl.elem.color,
              zIndex: 0,
            })
            .add();
          this.rects.push(lastRect);
        }
      } else if (lastBeforePoint && lastBeforePoint.elem) {
        //Если в текущем интервале нет точек, то прорисовываем один полигон между границами
        const barHeight = yAxis.toPixels(this.seriesY[sIndex]) - yAxis.toPixels(lastBeforePoint.y);
        const rect = chart.renderer
          .rect(
            xAxis.toPixels(extremes.min),
            yAxis.toPixels(lastBeforePoint.y),
            xAxis.toPixels(extremes.max) - xAxis.toPixels(extremes.min) + 1,
            barHeight,
            0,
          )
          .attr({
            fill: lastBeforePoint.elem.color,
            zIndex: 0,
          })
          .add();
        this.rects.push(rect);
      }
    });
  }
}
